import { useLocation } from "@remix-run/react";
import { useEffect } from "react";
import { CookieCategory } from "~/application/cookies/CookieCategory";
import { useRootData } from "~/utils/data/useRootData";
import CookieHelper from "~/utils/helpers/CookieHelper";

export default function ScriptAnalytics() {
  const rootData = useRootData();
  const location = useLocation();

  // Initialize consent mode v2 when component mounts or consent changes
  useEffect(() => {
    if (!rootData.appConfiguration?.analytics.enabled) {
      return;
    }
    
    // Initialize dataLayer if it doesn't exist
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      function gtag(...args: any[]) { window.dataLayer.push(arguments); }
      
      // Set default consent state (consent mode v2)
      gtag('consent', 'default', {
        'ad_storage': 'denied',
        'ad_user_data': 'denied', // New in v2
        'ad_personalization': 'denied', // New in v2
        'analytics_storage': 'denied'
      });
      
      // Update consent based on current user preferences
      const hasAnalyticsConsent = CookieHelper.hasConsent(rootData.userSession, CookieCategory.ANALYTICS);
      const hasFunctionalConsent = CookieHelper.hasConsent(rootData.userSession, CookieCategory.FUNCTIONAL);
      const hasAdvertisementConsent = CookieHelper.hasConsent(rootData.userSession, CookieCategory.ADVERTISEMENT);
      
      gtag('consent', 'update', {
        'analytics_storage': hasAnalyticsConsent ? 'granted' : 'denied',
        'ad_storage': hasAdvertisementConsent ? 'granted' : 'denied',
        'ad_user_data': hasAdvertisementConsent ? 'granted' : 'denied', // New in v2
        'ad_personalization': hasAdvertisementConsent ? 'granted' : 'denied', // New in v2
      });
    }
  }, [rootData.appConfiguration?.analytics.enabled, rootData.userSession.cookies]);

  if (!rootData.appConfiguration?.analytics.enabled) {
    console.log("[ScriptAnalytics] Analytics disabled in config");
    return null;
  }
  if (["/app/", "/admin/"].some((p) => location.pathname.startsWith(p))) {
    return null;
  }
  
  return (
    <>
      {/* Initialize dataLayer and gtag function FIRST */}
      <script
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            
            // Set default consent to 'denied' (consent mode v2)
            gtag('consent', 'default', {
              'ad_storage': 'denied',
              'ad_user_data': 'denied',
              'ad_personalization': 'denied', 
              'analytics_storage': 'denied'
            });
          `,
        }}
      />

      {rootData.appConfiguration?.analytics.simpleAnalytics && (
        <>
          <script async defer src="https://scripts.simpleanalyticscdn.com/latest.js"></script>
          <noscript>
            <img src="https://queue.simpleanalyticscdn.com/noscript.gif" alt="privacy-friendly-simpleanalytics" referrerPolicy="no-referrer-when-downgrade" />
          </noscript>
        </>
      )}

      {rootData.appConfiguration?.analytics.plausibleAnalytics && (
        <>
          <script defer data-domain={rootData.domainName} src="https://plausible.io/js/script.js"></script>
        </>
      )}

      {rootData.appConfiguration?.analytics.googleAnalyticsTrackingId && (
        <>
          <script 
            async 
            src={`https://www.googletagmanager.com/gtag/js?id=${rootData.appConfiguration?.analytics.googleAnalyticsTrackingId}`}
            onLoad={() => console.log("[ScriptAnalytics] Google Analytics script loaded")}
            onError={() => console.error("[ScriptAnalytics] Failed to load Google Analytics script")}
          />
          <script
            dangerouslySetInnerHTML={{
              __html: `
              console.log("[ScriptAnalytics] Initializing Google Analytics with ID: ${rootData.appConfiguration?.analytics.googleAnalyticsTrackingId}");
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${rootData.appConfiguration?.analytics.googleAnalyticsTrackingId}', {
                page_path: window.location.pathname,
              });
              console.log("[ScriptAnalytics] Google Analytics initialized");
            `,
            }}
          />
        </>
      )}
    </>
  );
}
